import { useState, useRef, useEffect } from 'react';
import { Send, Loader2, RefreshCw, ChevronRight, Upload, X, Check, FileText, Image, Smile, Edit } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Card } from './ui/card';
import { Avatar } from './ui/avatar';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { getAIResponse, extractWillDataFromResponse, streamAIResponse } from '../services/aiService';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

interface MediaFile {
  id: string;
  file: File;
  previewUrl: string;
  uploading: boolean;
  uploaded: boolean;
  error?: string;
}

interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

interface WillFormData {
  title: string;
  description: string;
  budget: string;
  currency: string;
  category: string;
  location: string;
  fulfillmentTimeType?: string;
  fulfillmentTime?: string;
  fulfillmentDate?: string;
  recurrencePattern?: string;
  budgetType?: string;
  media?: MediaFile[];
}

interface AIChatAssistantProps {
  onWillDataGenerated: (data: WillFormData) => void;
  onClose: () => void;
  onSubmitWill?: (data: WillFormData) => Promise<void>;
}

// Add localStorage keys for persistence
const AI_CHAT_MESSAGES_KEY = 'wanttd_ai_chat_messages';
const AI_CHAT_WILL_DATA_KEY = 'wanttd_ai_chat_will_data';
const AI_CHAT_MEDIA_KEY = 'wanttd_ai_chat_media';
const AI_CHAT_SUMMARY_KEY = 'wanttd_ai_chat_summary_visible';

const TypingIndicator = () => (
  <div className="typing-indicator">
    <span></span>
    <span></span>
    <span></span>
  </div>
);

const AIChatAssistant = ({ onWillDataGenerated, onClose, onSubmitWill }: AIChatAssistantProps) => {
  // Load saved messages from localStorage
  const loadSavedMessages = (): Message[] => {
    try {
      const savedMessages = localStorage.getItem(AI_CHAT_MESSAGES_KEY);
      if (savedMessages) {
        return JSON.parse(savedMessages);
      }
    } catch (error) {
      console.error('Error loading saved messages:', error);
    }
    return [{ 
      role: 'assistant', 
      content: 'Hi there! Please describe what you want in detail. I can help structure your request.' 
    }];
  };

  // Load saved willData from localStorage
  const loadSavedWillData = (): WillFormData | null => {
    try {
      const savedWillData = localStorage.getItem(AI_CHAT_WILL_DATA_KEY);
      if (savedWillData) {
        return JSON.parse(savedWillData);
      }
    } catch (error) {
      console.error('Error loading saved willData:', error);
    }
    return null;
  };

  // Load saved media from localStorage
  const loadSavedMedia = (): MediaFile[] => {
    try {
      const savedMedia = localStorage.getItem(AI_CHAT_MEDIA_KEY);
      if (savedMedia) {
        // Need to restore File objects which can't be stringified
        const parsedMedia = JSON.parse(savedMedia);
        // Return parsed media without File objects (these will need to be re-added when uploading)
        return parsedMedia;
      }
    } catch (error) {
      console.error('Error loading saved media:', error);
    }
    return [];
  };

  // Load saved summary visibility state
  const loadSummaryVisible = (): boolean => {
    try {
      const savedVisible = localStorage.getItem(AI_CHAT_SUMMARY_KEY);
      return savedVisible === 'true';
    } catch (error) {
      console.error('Error loading summary visibility:', error);
    }
    return false;
  };

  const [messages, setMessages] = useState<Message[]>(loadSavedMessages);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSummary, setShowSummary] = useState(loadSummaryVisible);
  const [willData, setWillData] = useState<WillFormData | null>(loadSavedWillData);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { language } = useLanguage();
  const isRTL = language === 'he';
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamedContent, setStreamedContent] = useState('');
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const [willMedia, setWillMedia] = useState<MediaFile[]>(loadSavedMedia);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update the button text based on language
  const postNowText = isRTL ? "פרסם עכשיו" : "Post Now";
  const editFirstText = isRTL ? "ערוך קודם" : "Edit First";
  const postingText = isRTL ? "מפרסם..." : "Posting...";
  const readyToPostText = isRTL ? "מוכן לפרסום" : "Ready to post";
  const wantPreviewText = isRTL ? "תצוגה מקדימה" : "Want Preview";

  // Save messages to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(AI_CHAT_MESSAGES_KEY, JSON.stringify(messages));
  }, [messages]);

  // Save willData to localStorage whenever it changes
  useEffect(() => {
    if (willData) {
      localStorage.setItem(AI_CHAT_WILL_DATA_KEY, JSON.stringify(willData));
    } else {
      localStorage.removeItem(AI_CHAT_WILL_DATA_KEY);
    }
  }, [willData]);

  // Save media to localStorage, but only metadata (not the actual files)
  useEffect(() => {
    if (willMedia.length > 0) {
      // Save only metadata, not actual File objects which can't be serialized
      const serializableMedia = willMedia.map(item => ({
        id: item.id,
        previewUrl: item.previewUrl,
        // Skip the file property
        uploading: item.uploading,
        uploaded: item.uploaded,
        error: item.error
      }));
      localStorage.setItem(AI_CHAT_MEDIA_KEY, JSON.stringify(serializableMedia));
    } else {
      localStorage.removeItem(AI_CHAT_MEDIA_KEY);
    }
  }, [willMedia]);

  // Save summary visibility state
  useEffect(() => {
    localStorage.setItem(AI_CHAT_SUMMARY_KEY, showSummary.toString());
  }, [showSummary]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamedContent]);

  // Add back the CSS styles for the typing indicator
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .typing-indicator {
        display: inline-flex;
        gap: 4px;
        padding: 4px 8px;
        background: #f0f0f0;
        border-radius: 10px;
      }
      
      .typing-indicator span {
        width: 4px;
        height: 4px;
        background: #666;
        border-radius: 50%;
        animation: bounce 1.4s infinite ease-in-out;
      }
      
      .typing-indicator span:nth-child(1) { animation-delay: -0.32s; }
      .typing-indicator span:nth-child(2) { animation-delay: -0.16s; }
      
      @keyframes bounce {
        0%, 80%, 100% { transform: scale(0); }
        40% { transform: scale(1); }
      }
      
      .ai-message {
        position: relative;
      }
      
      .ai-message.typing::after {
        content: '';
        width: 2px;
        height: 15px;
        background: #000;
        display: inline-block;
        animation: cursor-blink 0.8s infinite;
        margin-left: 2px;
        vertical-align: bottom;
      }
      
      @keyframes cursor-blink {
        0%, 100% { opacity: 0; }
        50% { opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Function to clean JSON and code blocks from message content
  const cleanMessageContent = (content: string): string => {
    // Remove JSON blocks (they will be shown in the summary card instead)
    let cleaned = content.replace(/```json\s*({[\s\S]*?})\s*```/g, '');
    
    // Remove any standalone JSON objects
    cleaned = cleaned.replace(/{[\s\S]*"title"[\s\S]*"description"[\s\S]*}/g, '');
    
    // If we've removed content and the message now has trailing/leading line breaks or commas, clean those up
    cleaned = cleaned.trim().replace(/^[\s,]+|[\s,]+$/g, '');
    
    return cleaned;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files).map(file => ({
        id: uuidv4(),
        file,
        previewUrl: URL.createObjectURL(file),
        uploading: false,
        uploaded: false
      }));
      
      setWillMedia([...willMedia, ...newFiles]);
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeMedia = (id: string) => {
    setWillMedia(prev => {
      const updated = prev.filter(item => item.id !== id);
      // Revoke object URLs to prevent memory leaks
      const removed = prev.find(item => item.id === id);
      if (removed) {
        URL.revokeObjectURL(removed.previewUrl);
      }
      return updated;
    });
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const newFiles = Array.from(e.dataTransfer.files)
        .filter(file => file.type.startsWith('image/'))
        .map(file => ({
          id: uuidv4(),
          file,
          previewUrl: URL.createObjectURL(file),
          uploading: false,
          uploaded: false
        }));
      
      if (newFiles.length > 0) {
        setWillMedia([...willMedia, ...newFiles]);
      } else {
        toast.error('Only image files are supported');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    // Create user message WITHOUT media
    const userMessage: Message = { 
      role: 'user' as const, 
      content: input
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    // Don't clear media here, as we want to keep it for the final Want
    setLoading(true);
    setIsStreaming(true);
    setStreamedContent('');

    try {
      let fullResponse = '';
      let accumulatedText = '';
      
      await streamAIResponse(
        [...messages, userMessage],
        (chunk) => {
          // Parse SSE format to extract just the text content
          const lines = chunk.split('\n');
          let textContent = '';
          
          for (const line of lines) {
            if (line.startsWith('data:')) {
              // Extract only the content after "data:" prefix
              const content = line.substring(5);
              textContent += content;
            }
          }
          
          // Accumulate the text for the complete response
          accumulatedText += textContent;
          
          // Update the streaming content
          setStreamedContent(accumulatedText);
          
          // Scroll to bottom smoothly with each new token
          setTimeout(() => lastMessageRef.current?.scrollIntoView({ behavior: 'smooth' }), 0);
          
          fullResponse = accumulatedText;
        },
        (complete) => {
          setIsStreaming(false);
          const cleanedResponse = cleanMessageContent(complete || fullResponse);
          const assistantMessage = { 
            role: 'assistant' as const, 
            content: cleanedResponse || complete || fullResponse
          };
          setMessages(prev => [...prev, assistantMessage]);
          setStreamedContent('');
          
          // Process will data
          const extractedData = extractWillDataFromResponse(complete || fullResponse);
          if (extractedData?.title && extractedData?.description) {
            setWillData({
              title: extractedData.title,
              description: extractedData.description,
              budget: extractedData.budget?.toString() || '',
              currency: extractedData.currency || '',
              category: extractedData.category || '',
              location: extractedData.location || '',
              fulfillmentTimeType: extractedData.fulfillmentTimeType || 'SPECIFIC_DATE',
              fulfillmentTime: extractedData.fulfillmentTime || '',
              fulfillmentDate: extractedData.fulfillmentDate || '',
              recurrencePattern: extractedData.recurrencePattern || 'NONE',
              budgetType: extractedData.budget ? 'FIXED' : (extractedData.budget === '0' ? 'FREE' : 'OPEN'),
              media: extractedData.media || []
            });
            setShowSummary(true);
          }
        }
      );
    } catch (error) {
      console.error('Error getting AI response:', error);
      toast.error('Failed to get AI response. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUseWillData = () => {
    if (willData) {
      // Create a new object that includes all fields
      const completeWillData: WillFormData = {
        ...willData,
        // Make sure to include all fields from WillForm
        fulfillmentTimeType: willData.fulfillmentTimeType || 'SPECIFIC_DATE',
        recurrencePattern: willData.recurrencePattern || 'NONE',
        budgetType: willData.budget ? 'FIXED' : (willData.budget === '0' ? 'FREE' : 'OPEN'),
        media: willMedia.length > 0 ? [...willMedia] : undefined
      };
      
      // Pass the complete data to the parent component
      onWillDataGenerated(completeWillData);
      
      // Scroll to top of page immediately before switching tabs
      window.scrollTo(0, 0);
    }
  };

  const handleSubmitWill = async () => {
    console.log('Submit will button clicked', { willData, onSubmitWill: !!onSubmitWill });
    
    if (!willData) {
      toast.error('No want data available. Please try again.');
      return;
    }
    
    if (!onSubmitWill) {
      toast.error('Cannot create want directly. Please use the Edit option instead.');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Extract only the valid media files from the willMedia array
      const mediaFiles = willMedia
        .filter(item => item && item.file instanceof File)
        .map(item => item.file);
      
      // Create a complete data object with all required fields
      const completeWillData: WillFormData = {
        ...willData,
        title: willData.title || 'New Want',
        description: willData.description || '',
        budget: willData.budget || '0',
        currency: willData.currency || 'ILS',
        category: willData.category || '',
        location: willData.location || '',
        fulfillmentTimeType: willData.fulfillmentTimeType || 'SPECIFIC_DATE',
        recurrencePattern: willData.recurrencePattern || 'NONE',
        budgetType: willData.budgetType || 'FIXED',
        // Don't include any media in the JSON payload
        media: undefined
      };
      
      // Pass the media files as MediaFile[] to match expected type
      await onSubmitWill({
        ...completeWillData,
        media: mediaFiles.map(file => ({
          id: uuidv4(),
          file,
          previewUrl: URL.createObjectURL(file),
          uploading: false,
          uploaded: false
        }))
      });
      
      // Success is handled by the parent component
    } catch (error) {
      console.error("Error submitting want:", error);
      toast.error("Failed to create want. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetChat = () => {
    // Clear localStorage
    localStorage.removeItem(AI_CHAT_MESSAGES_KEY);
    localStorage.removeItem(AI_CHAT_WILL_DATA_KEY);
    localStorage.removeItem(AI_CHAT_MEDIA_KEY);
    localStorage.removeItem(AI_CHAT_SUMMARY_KEY);

    // Reset state
    setMessages([
      { 
        role: 'assistant', 
        content: 'Hi there! Please describe what you want in detail. I can help structure your request.' 
      }
    ]);
    setShowSummary(false);
    setWillData(null);
    
    // Clear media and revoke object URLs
    willMedia.forEach(item => URL.revokeObjectURL(item.previewUrl));
    setWillMedia([]);
  };

  const Message = React.memo(({ content, role, isTyping = false }: { 
    content: string; 
    role: string; 
    isTyping?: boolean;
  }) => (
    <div className={`flex ${role === 'user' ? 'justify-end' : 'justify-start'}`}>
      <div className={`flex items-start max-w-[80%] ${
        role === 'user' 
          ? 'bg-primary/10 rounded-l-lg rounded-tr-lg' 
          : 'bg-gray-100 rounded-r-lg rounded-tl-lg'
      } p-3`}>
        {role === 'assistant' && (
          <Avatar className="h-8 w-8 mr-2 bg-primary/20">
            <span className="text-xs font-bold">AI</span>
          </Avatar>
        )}
        <div>
          <div 
            className={`text-sm whitespace-pre-wrap ai-message ${isTyping ? 'typing' : ''}`}
            dangerouslySetInnerHTML={{
              __html: content
                .replace(/\n/g, '<br>')
                .replace(/```(?!json)(.*?)```/gs, '<pre class="bg-gray-800 text-white p-2 rounded my-2 overflow-x-auto text-xs">$1</pre>')
                .replace(/```json\s*({[\s\S]*?})\s*```/g, '')
            }}
          />
        </div>
      </div>
    </div>
  ));

  const addEmoji = (emoji: any) => {
    setInput(prev => prev + emoji.native);
    setShowEmojiPicker(false);
    // Focus back on textarea after emoji selection
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="flex items-center justify-between p-4 border-b">
        <h3 className="text-lg font-medium">AI Assistant</h3>
        <Button variant="ghost" size="sm" onClick={resetChat}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Reset Chat
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4" ref={messagesEndRef}>
        {messages.map((message, index) => (
          <Message 
            key={index}
            content={message.content}
            role={message.role}
          />
        ))}
        
        {isStreaming && streamedContent && (
          <div ref={lastMessageRef}>
            <Message
              content={streamedContent}
              role="assistant"
              isTyping={true}
            />
          </div>
        )}
        
        {loading && !streamedContent && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-r-lg rounded-tl-lg p-3">
              <TypingIndicator />
            </div>
          </div>
        )}
      </div>

      {showSummary && willData && (
        <Card className="m-4 overflow-hidden shadow-lg border-0 bg-gradient-to-br from-indigo-50 via-blue-50 to-sky-50">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 flex items-center justify-between">
            <h4 className="font-semibold text-white text-lg flex items-center">
              <Check className="mr-2 h-5 w-5 bg-white text-blue-600 rounded-full p-1" />
              {wantPreviewText}
            </h4>
            <div className="bg-white/20 text-white text-xs px-3 py-1 rounded-full font-medium backdrop-blur-sm">
              {readyToPostText}
            </div>
          </div>
          
          <div className="p-5 space-y-4">
            <div className="bg-white rounded-lg shadow-sm p-4 border border-blue-100">
              <div className="text-sm text-blue-500 uppercase font-semibold tracking-wider mb-1">Title</div>
              <h3 className="text-gray-900 font-medium text-lg">{willData.title}</h3>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm p-4 border border-blue-100">
              <div className="text-sm text-blue-500 uppercase font-semibold tracking-wider mb-1">Description</div>
              <div className="text-gray-700 whitespace-pre-wrap">{willData.description}</div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {willData.category && (
                <div className="bg-white rounded-lg shadow-sm p-4 border border-blue-100">
                  <div className="text-sm text-blue-500 uppercase font-semibold tracking-wider mb-1">Category</div>
                  <div className="text-gray-700 flex items-center">
                    <span className="inline-block w-3 h-3 rounded-full bg-indigo-500 mr-2"></span>
                    {willData.category}
                  </div>
                </div>
              )}
              
              {willData.location && (
                <div className="bg-white rounded-lg shadow-sm p-4 border border-blue-100">
                  <div className="text-sm text-blue-500 uppercase font-semibold tracking-wider mb-1">Location</div>
                  <div className="text-gray-700 flex items-center">
                    <span className="inline-block w-3 h-3 rounded-full bg-green-500 mr-2"></span>
                    {willData.location}
                  </div>
                </div>
              )}
            </div>
            
            {/* Time section with detailed information */}
            {(willData.fulfillmentTimeType || willData.fulfillmentTime) && (
              <div className="bg-white rounded-lg shadow-sm p-4 border border-blue-100">
                <div className="text-sm text-blue-500 uppercase font-semibold tracking-wider mb-1">When</div>
                <div className="space-y-1">
                  {willData.fulfillmentTimeType && (
                    <div className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                        willData.fulfillmentTimeType === 'ASAP' ? 'bg-red-100 text-red-600' :
                        willData.fulfillmentTimeType === 'THIS_WEEK' ? 'bg-orange-100 text-orange-600' :
                        'bg-blue-100 text-blue-600'
                      }`}>
                        {willData.fulfillmentTimeType === 'ASAP' ? '!' : 
                         willData.fulfillmentTimeType === 'THIS_WEEK' ? '7' : 
                         '📅'}
                      </div>
                      <div>
                        <div className="text-gray-800 font-medium">
                          {willData.fulfillmentTimeType === 'ASAP' ? 'As soon as possible' : 
                           willData.fulfillmentTimeType === 'THIS_WEEK' ? 'This week' : 
                           'Specific date'}
                        </div>
                        {willData.fulfillmentTime && willData.fulfillmentTimeType === 'SPECIFIC_DATE' && (
                          <div className="text-gray-500 text-sm">
                            {new Date(willData.fulfillmentTime).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {willData.recurrencePattern && willData.recurrencePattern !== 'NONE' && (
                    <div className="flex items-center mt-2 border-t border-gray-100 pt-2">
                      <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center mr-3">
                        ↻
                      </div>
                      <div>
                        <div className="text-gray-800">
                          Recurring: {willData.recurrencePattern.charAt(0) + willData.recurrencePattern.slice(1).toLowerCase()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Budget section with detailed information */}
            <div className="bg-white rounded-lg shadow-sm p-4 border border-blue-100">
              <div className="text-sm text-blue-500 uppercase font-semibold tracking-wider mb-1">Budget</div>
              <div className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                  willData.budgetType === 'FREE' ? 'bg-green-100 text-green-600' :
                  willData.budgetType === 'FIXED' ? 'bg-blue-100 text-blue-600' :
                  'bg-purple-100 text-purple-600'
                }`}>
                  {willData.budgetType === 'FREE' ? '₪0' : 
                   willData.budgetType === 'FIXED' ? '₪' : 
                   '~'}
                </div>
                <div>
                  <div className={`text-gray-800 font-medium ${willData.budgetType === 'FIXED' ? 'text-lg' : ''}`}>
                    {willData.budgetType === 'FIXED' ? (
                      `${willData.budget} ${willData.currency}`
                    ) : willData.budgetType === 'FREE' ? (
                      'FREE'
                    ) : willData.budgetType === 'OPEN' ? (
                      'Open to offers'
                    ) : (
                      willData.budget ? `${willData.budget} ${willData.currency}` : 'Not specified'
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {willMedia.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-4 border border-blue-100">
                <div className="text-sm text-blue-500 uppercase font-semibold tracking-wider mb-2">Media</div>
                <div className="flex flex-wrap gap-3">
                  {willMedia.map(item => (
                    <div key={item.id} className="relative">
                      <img 
                        src={item.previewUrl} 
                        alt="Attached media" 
                        className="w-20 h-20 object-cover rounded-md border border-gray-200 hover:border-blue-300 transition-colors"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 flex justify-between">
            <Button 
              variant="outline" 
              onClick={handleUseWillData} 
              className="border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            >
              <Edit className="mr-2 h-4 w-4" />
              {editFirstText}
            </Button>
            <Button 
              variant="default" 
              onClick={handleSubmitWill} 
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white transition-colors shadow-md hover:shadow-lg px-6"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {postingText}
                </>
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  {postNowText}
                </>
              )}
            </Button>
          </div>
        </Card>
      )}

      {willMedia.length > 0 && (
        <div className="px-4 pt-2">
          <div className="flex flex-wrap gap-2 mb-2">
            {willMedia.map(item => (
              <div key={item.id} className="relative">
                <img 
                  src={item.previewUrl} 
                  alt="Media preview" 
                  className="w-16 h-16 object-cover rounded-md border border-gray-200"
                />
                <button 
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 w-5 h-5 flex items-center justify-center"
                  onClick={() => removeMedia(item.id)}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
          <div className="text-xs text-gray-500 mb-2">
            Media will be attached to your Want when you click "Use This Want"
          </div>
        </div>
      )}

      <form 
        onSubmit={handleSubmit} 
        className="p-4 border-t"
      >
        <div 
          className={`flex flex-col gap-2 ${isDragging ? 'bg-blue-50 border-2 border-dashed border-blue-300 rounded-lg p-2' : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <div className="relative">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={isDragging ? "Drop images here..." : "Describe what you want..."}
              className={`flex-1 min-h-[100px] resize-none ${isRTL ? 'text-right' : 'text-left'} pr-24`}
              dir={isRTL ? "rtl" : "ltr"}
            />
            <div className="absolute bottom-2 right-2 flex items-center space-x-2">
              <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                  >
                    <Smile className="h-5 w-5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0 border-none shadow-xl" align="end">
                  <Picker 
                    data={data} 
                    onEmojiSelect={addEmoji}
                    theme="light"
                    previewPosition="none"
                    skinTonePosition="none"
                  />
                </PopoverContent>
              </Popover>
              
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                className="hidden"
                multiple
                accept="image/*"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
              >
                <Image className="h-5 w-5" />
              </button>
              
              <Button 
                type="submit" 
                disabled={loading || !input.trim()} 
                className="rounded-full w-8 h-8 p-0 flex items-center justify-center"
                variant={input.trim() ? "default" : "ghost"}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </form>

      <div className="text-xs text-center text-gray-400 pb-2">
        Drag and drop images or click the image icon to upload
      </div>
    </div>
  );
};

export default AIChatAssistant;